import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  FormControlLabel,  
  Checkbox, 
  Switch,
  ListSubheader,
  Card,
  CardContent,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  FileText,
  HelpCircle,
  X,
  Eye,
  Plus,
  Trash2,
  Wand2,
  Settings,
  Image, 
  CheckCircle,
  AlertCircle,
  Sparkles,
  Lightbulb,
  MessageSquare,
  Search,
  Zap,
  User,
  Users,
  Bookmark,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import PageHeader from './PageHeader';
import ImageControls from './ImageControls';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';
import WordPressClient from '../utils/WordPressClient';
import aiClient from '../api/clients/AiClient';
import imageClient from '../api/clients/ImageClient';
import formatArticleContent from '../utils/articleFormatting';
import { useAIModels } from '../hooks/useAIModels';
import ModelSelector from './ModelSelector';

// Response dialog for showing success or error after article generation
const ArticleResponseDialog = ({ 
  open, 
  onClose, 
  success, 
  error, 
  articleData,
  articlesCount = 1,
  onPreview 
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        p: 3,
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        textAlign: 'center'
      }}>
        <Box sx={{ 
          width: 70, 
          height: 70, 
          borderRadius: '50%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 2,
          background: success ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.2))' : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.2))',
          color: success ? '#10B981' : '#EF4444',
          boxShadow: success ? '0 8px 16px rgba(16, 185, 129, 0.15)' : '0 8px 16px rgba(239, 68, 68, 0.15)',
          animation: success ? 'pulse 1.5s infinite' : 'none',
          '@keyframes pulse': {
            '0%': {
              boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.4)'
            },
            '70%': {
              boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)'
            },
            '100%': {
              boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)'
            }
          }
        }}>
          {success ? (
            <CheckCircle size={36} strokeWidth={2} />
          ) : (
            <AlertCircle size={36} strokeWidth={2} />
          )}
        </Box>
        
        <Typography variant="h5" sx={{ 
          fontWeight: 700,
          mb: 1,
          color: '#1F2937',
          fontSize: '1.5rem'
        }}>
          {success ? (articlesCount > 1 ? `${articlesCount} Articles Created` : 'Article Created') : 'Generation Failed'}
        </Typography>
           
        <Typography variant="body1" sx={{ 
          color: 'text.secondary',
          mb: 3,
          maxWidth: '90%',
          lineHeight: 1.6
        }}>
          {success 
            ? `Your ${articlesCount > 1 ? `${articlesCount} articles have` : 'article has'} been successfully created and saved as ${articlesCount > 1 ? 'drafts' : 'a draft'} in WordPress.` 
            : error || 'An error occurred while generating your article.'}
        </Typography>
        
        {success && (
          <Box sx={{ 
            width: '100%',
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            mt: 1
          }}>
            <Button 
              onClick={onPreview}
              variant="outlined"
              startIcon={<Eye size={18} />}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '12px',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                color: '#1F2937',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#3B82F6',
                  bgcolor: 'rgba(59, 130, 246, 0.04)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {articlesCount > 1 ? 'Preview Last Article' : 'Preview Article'}
            </Button>
            <Button
              onClick={onClose}
              variant="contained"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '12px',
                bgcolor: '#3B82F6',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transition: 'all 0.6s ease',
                },
                '&:hover': {
                  bgcolor: '#2563EB',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                  '&:before': {
                    left: '100%',
                  }
                }
              }}
            >
              Create Another Article
            </Button>
          </Box>
        )}
        
        {!success && (
          <Button 
            onClick={onClose}
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: '12px',
              bgcolor: '#3B82F6',
              fontWeight: 500,
              mt: 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#2563EB',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
              }
            }}
          >
            Try Again
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

// Styled component for article preview content
const StyledContent = styled(DialogContent)(({ theme }) => ({
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    margin: '1rem 0',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
    fontWeight: 600,
  },
  '& p': {
    marginBottom: '1rem',
    lineHeight: 1.7,
  },
}));

// Dialog for previewing the generated article
const ArticlePreviewDialog = ({ open, onClose, article, articlesCount = 1 }) => {
  if (!article) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="h6">Article Preview</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <StyledContent>
        {articlesCount > 1 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You generated {articlesCount} articles. This is a preview of the last article that was created.
          </Alert>
        )}
        
        <Typography variant="h4" gutterBottom>
          {article.title}
        </Typography>
        
        {article.featuredImage && (
          <Box sx={{ my: 2 }}>
            <img 
              src={article.featuredImage} 
              alt={article.title}
              style={{ 
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px'
              }} 
            />
          </Box>
        )}

        <Box 
          sx={{ mt: 3 }}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </StyledContent>

      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 2
      }}>
        <Button onClick={onClose}>
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LoadingOverlay = ({ visible, status, steps }) => {
  if (!visible) return null;
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 500,
          width: '100%',
          bgcolor: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          p: 4,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
            mb: 3,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -5,
              left: -5,
              right: -5,
              bottom: -5,
              borderRadius: '50%',
              border: '2px solid rgba(224, 76, 22, 0.2)',
              animation: 'pulse 2s infinite',
            },
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              color: 'white',
            }}
          />
        </Box>
        
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1F2937' }}>
          Creating Your Article
        </Typography>
        
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
          {status || 'Please wait while we generate your article...'}
        </Typography>
        
        <Box sx={{ width: '100%', mb: 2 }}>
          {steps.map((step) => (
            <Box
              key={step.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: step.status === 'pending' ? 0.5 : 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  mr: 2,
                  fontSize: '0.8rem',
                  ...(step.status === 'completed'
                    ? {
                        bgcolor: '#10B981',
                        color: 'white',
                      }
                    : step.status === 'in-progress'
                    ? {
                        bgcolor: '#3B82F6',
                        color: 'white',
                      }
                    : step.status === 'error'
                    ? {
                        bgcolor: '#EF4444',
                        color: 'white',
                      }
                    : {
                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                        color: 'text.secondary',
                      }),
                }}
              >
                {step.status === 'completed' ? (
                  <CheckCircle size={16} />
                ) : step.status === 'error' ? (
                  <AlertCircle size={16} />
                ) : step.status === 'in-progress' ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : (
                  step.icon
                )}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: step.status === 'error' ? '#EF4444' : 'text.primary',
                  }}
                >
                  {step.text}
                </Typography>
                {step.message && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {step.message}
                  </Typography>
                )}
              </Box>
              {step.status === 'completed' && <CheckCircle size={16} color="#10B981" />}
              {step.status === 'error' && <AlertCircle size={16} color="#EF4444" />}
            </Box>
          ))}
        </Box>
        
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontStyle: 'italic',
            mt: 2,
          }}
        >
          This may take a minute or two depending on the article length
        </Typography>
      </Box>
    </Box>
  );
};

const QuickArticleCreator = ({ onGeneratingChange }) => {
  const { authenticatedRequest, error: authError, loading: authLoading } = useAuth();
  const { models: availableModels, loading: modelsLoading, error: modelsError } = useAIModels(authenticatedRequest);
  
  // Main component state and logic will go here
  const [inputMode, setInputMode] = useState('title');
  const [titleInput, setTitleInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [titleType, setTitleType] = useState('general');
  const [generatedTitles, setGeneratedTitles] = useState([]);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [isGeneratingTitles, setIsGeneratingTitles] = useState(false);
  const [includeImages, setIncludeImages] = useState(true);
  const [includeFAQs, setIncludeFAQs] = useState(true);
  const [writingStyle, setWritingStyle] = useState('informative');
  const [perspective, setPerspective] = useState('third');
  const [articleLength, setArticleLength] = useState('medium');
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [imageModel, setImageModel] = useState('fal-ai/flux/schnell');
  const [imageStyle, setImageStyle] = useState('realistic');
  const [dimensions, setDimensions] = useState('square');
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState(null);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationSteps, setGenerationSteps] = useState([
    { id: 'init', text: 'Initializing content generation', status: 'pending', icon: <Settings size={16} /> },
    { id: 'content', text: 'Generating article content', status: 'pending', icon: <FileText size={16} /> },
    { id: 'images', text: 'Processing images', status: 'pending', icon: <Image size={16} /> },
    { id: 'upload', text: 'Publishing to WordPress', status: 'pending', icon: <ArrowRight size={16} /> }
  ]);
  const [subheadings, setSubheadings] = useState([]);
  const [isGeneratingSubheadings, setIsGeneratingSubheadings] = useState(false);
  const [subheadingType, setSubheadingType] = useState('auto');
  const [customSubheadings, setCustomSubheadings] = useState('');
  const [includeSubheadingImages, setIncludeSubheadingImages] = useState(false);
  const [maxSubheadingImages, setMaxSubheadingImages] = useState(3);
  const [articleProgress, setArticleProgress] = useState({});
  const [subheadingCount, setSubheadingCount] = useState(5); // Default to 5 subheadings
  
  // SEO Keywords state
  const [seoKeywords, setSeoKeywords] = useState('');
  const [keywordMode, setKeywordMode] = useState('manual'); // 'manual' or 'generate'
  const [generatedKeywords, setGeneratedKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);
  
  // Initialize data on component mount
  useEffect(() => {
    if (!authLoading) {
      fetchInitialData();
      initializeServices();
    }
  }, [authLoading]);
  
  // Update categories when site changes
  useEffect(() => {
    if (selectedSite) {
      fetchCategories();
    }
  }, [selectedSite]);
  
  // Initialize AI services when models are available
  useEffect(() => {
    const initializeAI = async () => {
      if (availableModels.length > 0) {
        try {
          const keys = await authenticatedRequest(() => apiClient.getApiKeys());
          
          // Initialize AI client
          aiClient.setApiKeys(keys);
          aiClient.updateModelConfigs(availableModels);
          
          // Initialize image client
          imageClient.setAvailableModels(availableModels);
          imageClient.setAiClient(aiClient);
          imageClient.setApiKeys(keys);
        } catch (error) {
          console.error('Error initializing AI client:', error);
          setError('Failed to initialize AI services');
        }
      }
    };
  
    initializeAI();
  }, [availableModels, authenticatedRequest]);
  
  // Update generation status in parent component
  useEffect(() => {
    onGeneratingChange?.(isGenerating);
    return () => onGeneratingChange?.(false);
  }, [isGenerating, onGeneratingChange]);
  
  // Helper function to get auxiliary model
  const getAuxiliaryModel = () => {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return availableModels[0]?.value;
  };
  
  // Initialize services
  const initializeServices = async () => {
    try {
      const keys = await authenticatedRequest(() => apiClient.getApiKeys());
      
      if (!keys.openai && !keys.anthropic && !keys.fal && !keys.openrouter) {
        throw new Error("No API keys configured. Please add required API keys in settings.");
      }
  
      // Initialize image generation services
      if (keys.fal) {
        imageClient.setApiKeys(keys);
        console.log('Image generation services initialized');
      } else {
        console.warn('Fal.ai API key not configured - image generation will be disabled');
        setIncludeImages(false);
      }
    } catch (error) {
      console.error("Failed to initialize services:", error);
      const errorMessage = error.message.includes('API keys') 
        ? error.message 
        : 'Failed to initialize services. Please check your API keys in settings.';
      setError(errorMessage);
    }
  };
  
  // Fetch initial data (WordPress sites and categories)
  const fetchInitialData = async () => {
    try {
      const sitesData = await authenticatedRequest(() => apiClient.getWordPressSites());
      setSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0].id);
      }
    } catch (err) {
      setError('Failed to load WordPress sites');
    }
  };
  
  // Fetch categories for selected site
  const fetchCategories = async () => {
    if (!selectedSite) return;
    try {
      const categoriesData = await authenticatedRequest(() => 
        apiClient.getCategories(selectedSite)
      );
      setCategories(categoriesData);
      if (categoriesData.length > 0) {
        setCategory(categoriesData[0].id);
      }
    } catch (err) {
      setError('Failed to load categories');
    }
  };
  
  // Generate article titles based on keyword or title input
  const handleGenerateTitles = async () => {
    if (!keyword.trim()) {
      setError('Please enter a keyword');
      return;
    }
    
    setIsGeneratingTitles(true);
    setError(null);
    
    try {
      const auxModel = getAuxiliaryModel();
      if (!auxModel) {
        throw new Error('No auxiliary model available');
      }
      
      const prompt = `Generate 20 engaging article titles about "${keyword}". 
        ${titleType === 'question' ? 'Format them as questions.' : ''}
        ${titleType === 'how-to' ? 'Format them as how-to guides.' : ''}
        ${titleType === 'list' ? 'Format them as numbered lists.' : ''}
        ${titleType === 'seo' ? 'Optimize them for search engines with relevant keywords.' : ''}
        Return only the titles, one per line.`;
      
      const response = await aiClient.generateArticleContent({
        title: `Titles about ${keyword}`,
        modelId: auxModel,
        customPrompt: prompt
      });
      
      const titles = response
        .split('\n')
        .filter(line => line.trim())
        .map(title => {
          // Remove numbering if present (e.g., "1. Title")
          let cleanTitle = title.replace(/^\d+\.\s*/, '').trim();
          
          // Remove quotes around the title (both single and double quotes)
          cleanTitle = cleanTitle.replace(/^["'](.*)["']$/, '$1').trim();
          
          return cleanTitle;
        })
        .filter(title => title.length > 0);
      
      setGeneratedTitles(titles);
      setSelectedTitles(titles.length > 0 ? [titles[0]] : []);
    } catch (err) {
      console.error('Title generation error:', err);
      setError('Failed to generate titles: ' + err.message);
    } finally {
      setIsGeneratingTitles(false);
    }
  };
  
  // Generate SEO keywords based on title(s)
  const handleGenerateKeywords = async () => {
    const titleToUse = selectedTitles.length > 0 ? selectedTitles[0] : titleInput;
    
    if (!titleToUse.trim()) {
      setError('Please enter a title or select one first');
      return;
    }
    
    setIsGeneratingKeywords(true);
    setError(null);
    
    try {
      const auxModel = getAuxiliaryModel();
      if (!auxModel) {
        throw new Error('No auxiliary model available');
      }
      
      const prompt = `Generate 15 SEO keywords and phrases for an article titled "${titleToUse}".
        Include a mix of:
        - Primary keywords (1-2 words)
        - Long-tail keywords (3-5 words)
        - Question-based keywords
        - Keywords with high search volume potential
        
        Return only the keywords, one per line, without numbering.`;
      
      const response = await aiClient.generateArticleContent({
        title: `Keywords for ${titleToUse}`,
        modelId: auxModel,
        customPrompt: prompt
      });
      
      const keywords = response
        .split('\n')
        .filter(line => line.trim())
        .map(keyword => {
          // Remove quotes, bullets or other formatting
          let cleanKeyword = keyword.replace(/^[-*•]?\s*/, '').trim();
          cleanKeyword = cleanKeyword.replace(/^["'](.*)["']$/, '$1').trim();
          return cleanKeyword;
        })
        .filter(keyword => keyword.length > 0);
      
      setGeneratedKeywords(keywords);
      setSelectedKeywords(keywords.length > 0 ? [keywords[0]] : []);
    } catch (err) {
      console.error('Keyword generation error:', err);
      setError('Failed to generate keywords: ' + err.message);
    } finally {
      setIsGeneratingKeywords(false);
    }
  };
  
  // Handle toggling a title selection
  const handleTitleToggle = (title) => {
    setSelectedTitles(prev => {
      if (prev.includes(title)) {
        return prev.filter(t => t !== title);
      } else {
        return [...prev, title];
      }
    });
  };
  
  // Handle toggling a keyword selection
  const handleKeywordToggle = (keyword) => {
    setSelectedKeywords(prev => {
      if (prev.includes(keyword)) {
        return prev.filter(k => k !== keyword);
      } else {
        return [...prev, keyword];
      }
    });
  };
  
  // Handle article generation
  const handleGenerateArticle = async () => {
    if (selectedTitles.length === 0) {
      setError('Please select at least one title for your article');
      return;
    }
    
    if (!selectedSite) {
      setError('Please select a WordPress site');
      return;
    }
    
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setLoading(true);
    setIsGenerating(true);
    setError(null);
    
    // Initialize progress tracking for each title
    const initialProgress = {};
    selectedTitles.forEach(title => {
      initialProgress[title] = { status: 'pending', step: 'init', message: 'Waiting to start...' };
    });
    setArticleProgress(initialProgress);
    
    // Enhanced generation steps with more detailed information
    setGenerationSteps([
      { id: 'init', text: 'Preparing Content Framework', status: 'pending', icon: <Settings size={16} /> },
      { id: 'content', text: 'Crafting Article Content', status: 'pending', icon: <FileText size={16} /> },
      { id: 'images', text: 'Generating & Placing Images', status: 'pending', icon: <Image size={16} /> },
      { id: 'upload', text: 'Publishing to WordPress', status: 'pending', icon: <ArrowRight size={16} /> },
    ]);
    
    // Enhanced step status update function with more detailed information
    const updateStepStatus = (stepId, status, message = null, error = null) => {
      setGenerationSteps((prev) =>
        prev.map((step) =>
          step.id === stepId ? { ...step, status, message, error } : step
        )
      );
    };
    
    // New function to update article progress
    const updateArticleProgress = (title, status, step, message) => {
      setArticleProgress(prev => ({
        ...prev,
        [title]: { status, step, message }
      }));
    };
    
    try {
      setGenerationStatus('Initializing article generation process...');
      updateStepStatus('init', 'in-progress', 'Setting up generation environment');
      
      // Create the article generation prompt
      const createArticlePrompt = (title) => {
        const styleInstructions = {
          informative: 'Use a clear, factual tone focused on providing valuable information. Be authoritative yet accessible.',
          conversational: 'Use a friendly, engaging tone as if speaking directly to the reader. Include rhetorical questions and relatable examples.',
          professional: 'Use a formal, authoritative tone appropriate for business or academic contexts. Include data-backed statements and precise terminology.'
        };
        
        const perspectiveInstructions = {
          first: 'Write from a first-person perspective (using "I" and "we"). Share personal insights where appropriate.',
          second: 'Write from a second-person perspective (addressing the reader as "you"). Make the content directly relevant to the reader.',
          third: 'Write from a third-person perspective (using "he", "she", "they", "it"). Maintain objective distance when presenting information.'
        };
        
        // More precise word count allocation by section based on subheading count
        const lengthInstructions = {
          short: `Total word count: 600-700 words.
            - Introduction: 60-80 words
            - ${subheadingCount} main sections: ${Math.floor(450/subheadingCount)}-${Math.floor(550/subheadingCount)} words each
            - Conclusion: 60-80 words
            ${includeFAQs ? '- FAQ section: 150-200 words (5 Q&As)' : ''}`,
          medium: `Total word count: 1000-1200 words.
            - Introduction: 100-120 words
            - ${subheadingCount} main sections: ${Math.floor(700/subheadingCount)}-${Math.floor(900/subheadingCount)} words each
            - Conclusion: 100-120 words
            ${includeFAQs ? '- FAQ section: 200-250 words (5 Q&As)' : ''}`,
          long: `Total word count: 1500-1800 words.
            - Introduction: 150-200 words
            - ${subheadingCount} main sections: ${Math.floor(1100/subheadingCount)}-${Math.floor(1350/subheadingCount)} words each
            - Conclusion: 150-200 words
            ${includeFAQs ? '- FAQ section: 300-350 words (5 Q&As)' : ''}`
        };
        
        let subheadingInstructions = '';
        if (subheadingType === 'auto') {
          subheadingInstructions = `Create exactly ${subheadingCount} descriptive and engaging H2 subheadings that cover all key aspects of the topic.`;
        } else if (subheadingType === 'custom' && customSubheadings.trim()) {
          const subheadingsList = customSubheadings
            .split('\n')
            .filter(line => line.trim())
            .map(heading => `- ${heading.trim()}`)
            .join('\n');
          
          subheadingInstructions = `Use exactly the following subheadings in this order as H2 headings:\n${subheadingsList}`;
        }
        
        // Prepare SEO keywords instructions
        const keywordsToUse = keywordMode === 'manual' 
          ? seoKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
          : selectedKeywords;
        
        let seoInstructions = '';
        if (keywordsToUse.length > 0) {
          seoInstructions = `
SEO OPTIMIZATION:
- Naturally incorporate the following keywords throughout the article:
${keywordsToUse.map(k => `  - "${k}"`).join('\n')}
- Include primary keywords in the introduction and conclusion
- Use keywords in some subheadings where appropriate
- Maintain a natural flow - don't force keywords where they don't fit
- Aim for keyword density of 1-2% for primary keywords
`;
        }
        
        return `Write a comprehensive, educational article with the title: "${title}"

FORMAT AND STRUCTURE REQUIREMENTS:
- Do not repeat the article title at the start
- Use <h2> tags for all main section headings
- Keep paragraphs short (2-3 sentences) for readability
- Use topic sentences to begin each paragraph
- Maintain a clear progression of ideas
- Include smooth transitions between sections
- Do NOT duplicate content between sections

WRITING STYLE GUIDELINES:
- ${styleInstructions[writingStyle]}
- ${perspectiveInstructions[perspective]}
- Explain complex concepts in simple terms
- Use analogies to explain difficult ideas
- Balance technical details with accessible explanations
- Provide examples and practical applications
- Address common questions and misconceptions

SECTION REQUIREMENTS:
- Begin with a compelling introduction that hooks the reader (don't start with "Introduction" heading)
- The introduction should appear ONLY ONCE at the beginning of the article
- ${subheadingInstructions}
- Begin each section with a clear introduction that's unique to that section
- Do NOT repeat the main introduction in any section
- Include specific examples and details in every section
- Support main points with evidence or explanation
- End sections with a connection to the next topic
- Conclude with a strong, actionable summary

${seoInstructions}

CONTENT SPECIFICATIONS:
${lengthInstructions[articleLength]}

CONTENT QUALITY:
- Use precise, factual information
- Include practical examples and real-world applications
- Address common questions and concerns
- Provide actionable takeaways in each section
- Reference expert insights where appropriate
- Ensure NO duplicate content appears across sections

${includeFAQs ? `FAQ SECTION REQUIREMENTS:
- Add an "FAQ" or "Frequently Asked Questions" section at the end with <h2> heading
- Include 5 relevant and insightful Q&As about the topic
- Format each question in <strong> tags
- Provide comprehensive yet concise answers (2-3 sentences each)
- Address common misconceptions or concerns
- Do NOT repeat content from the introduction or main sections` : ''}

HTML FORMATTING REQUIREMENTS:
- Use <h2> tags for main sections (not ## markdown)
- Each paragraph must be wrapped in <p> tags
- Use <ul> and <li> for unordered lists, <ol> and <li> for ordered lists
- Use <strong> for emphasis instead of <b>
- Use <em> for italics instead of <i>
- Do NOT include the title in an <h1> tag - it will be added automatically
- Do NOT include any <html>, <head>, or <body> tags

The content will be published directly to WordPress, so ensure the HTML is clean and properly formatted.`;
      };
      
      updateStepStatus('init', 'completed', 'Setup complete');
      
      // Process each selected title with enhanced feedback
      let completedArticles = 0;
      const totalArticles = selectedTitles.length;
      
      for (const title of selectedTitles) {
        completedArticles++;
        setGenerationStatus(`Generating article ${completedArticles}/${totalArticles}: "${title}"`);
        updateStepStatus('content', 'in-progress', `Creating content for "${title}" (${completedArticles}/${totalArticles})`);
        updateArticleProgress(title, 'in-progress', 'content', `Creating content (${completedArticles}/${totalArticles})`);
        
        const auxModel = getAuxiliaryModel();
        const articlePrompt = createArticlePrompt(title);
        
        const rawContent = await aiClient.generateArticleContent({
          title: title,
          modelId: auxModel,
          customPrompt: articlePrompt,
        });
        
        updateStepStatus('content', 'completed', `Content created for "${title}"`);
        updateArticleProgress(title, 'in-progress', 'images', 'Processing images');
        updateStepStatus('images', 'in-progress', 'Processing images');
        
        console.log('Raw content length:', rawContent.length);
        console.log('Raw content sample:', rawContent.substring(0, 200) + '...');
        
        // Process the content with our specialized article formatter
        let processedContent = formatArticleContent(rawContent);
        console.log('Processed content length:', processedContent.length);
        console.log('Processed content sample:', processedContent.substring(0, 200) + '...');
        
        // Ensure the content is properly formatted for WordPress
        // Sometimes the content might still have HTML, HEAD, BODY tags
        if (processedContent.includes('<html') || processedContent.includes('<body')) {
          console.log('Content contains HTML/BODY tags, extracting inner content...');
          const tempDoc = document.createElement('div');
          tempDoc.innerHTML = processedContent;
          processedContent = tempDoc.innerHTML;
        }
        
        const site = sites.find((s) => s.id === selectedSite);
        if (!site) throw new Error('Selected site not found');
        
        const wpClient = new WordPressClient(site);
        const credentials = await authenticatedRequest(() =>
          apiClient.getWordPressCredentials(selectedSite)
        );
        wpClient.setCredentials(credentials.username, credentials.password);
        
        // Initialize featured media ID
        let featuredMediaId = null;
        
        // Handle featured image with enhanced feedback
        if (includeImages) {
          updateStepStatus('images', 'in-progress', `Generating featured image for "${title}"`);
          setGenerationStatus(`Creating featured image for "${title}"`);
          updateArticleProgress(title, 'in-progress', 'images', 'Creating featured image');
          try {
            // Create a more descriptive prompt for the featured image
            const imagePrompt = `High quality featured image for article: ${title}`;
            
            const imageOptions = {
              prompt: imagePrompt,
              model: imageModel,
              dimension: dimensions,
              style: imageStyle,
              enhancePrompt: true,
              context: { articleTitle: title },
            };
            
            console.log('Featured image options:', imageOptions);
            
            const imageData = await imageClient.generateImage(imageOptions);
            
            if (imageData?.image_url) {
              updateStepStatus('images', 'in-progress', 'Uploading featured image to WordPress');
              setGenerationStatus('Uploading featured image to WordPress...');
              const response = await fetch(imageData.image_url);
              const blob = await response.blob();
              const formData = new FormData();
              
              // Use a more descriptive filename
              const safeTitle = title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
              formData.append('file', blob, `featured-${safeTitle}-${Date.now()}.png`);
              formData.append('siteId', selectedSite);
              
              const uploadResult = await authenticatedRequest(() =>
                apiClient.uploadMedia(formData)
              );
              
              if (uploadResult?.media_id) {
                console.log(`Successfully uploaded featured image with ID: ${uploadResult.media_id}`);
                featuredMediaId = uploadResult.media_id;
                updateStepStatus('images', 'in-progress', 'Featured image uploaded successfully');
              } else {
                console.error('Media upload succeeded but no media_id was returned');
                updateStepStatus('images', 'in-progress', 'Featured image upload completed with warnings');
              }
            } else {
              console.error('No image_url in the response from image generation');
              updateStepStatus('images', 'in-progress', 'Featured image generation had issues, continuing');
              setGenerationStatus('Featured image generation failed, continuing...');
            }
          } catch (error) {
            console.error('Featured image error:', error);
            updateStepStatus('images', 'in-progress', 'Featured image generation failed, continuing');
            setGenerationStatus('Featured image generation failed, continuing...');
          }
        }
        
        // Handle subheading images if enabled with enhanced feedback
        if (includeSubheadingImages) {
          try {
            updateStepStatus('images', 'in-progress', 'Processing subheading images');
            setGenerationStatus('Adding images to article sections...');
            
            // Parse the HTML content to find subheadings
            const parser = new DOMParser();
            const doc = parser.parseFromString(processedContent, 'text/html');
            const headings = Array.from(doc.querySelectorAll('h2'));
            
            console.log(`Found ${headings.length} h2 headings for potential images`);
            
            // Skip the first heading (introduction) and space out the images
            let selectedHeadings = [];
            
            if (headings.length > 0) {
              // Skip the first heading (introduction)
              const availableHeadings = headings.slice(1);
              
              if (availableHeadings.length <= maxSubheadingImages) {
                // If we have fewer or equal headings than max images, use all available headings
                selectedHeadings = availableHeadings;
              } else {
                // Space out the images evenly throughout the article
                const interval = Math.floor(availableHeadings.length / maxSubheadingImages);
                
                for (let i = 0; i < maxSubheadingImages; i++) {
                  const index = i * interval;
                  if (index < availableHeadings.length) {
                    selectedHeadings.push(availableHeadings[index]);
                  }
                }
              }
            }
            
            console.log(`Processing ${selectedHeadings.length} headings for images, spaced throughout the article`);
            
            // Process each heading sequentially to ensure all images are inserted
            for (let i = 0; i < selectedHeadings.length; i++) {
              const heading = selectedHeadings[i];
              const headingText = heading.textContent;
              
              updateStepStatus('images', 'in-progress', `Creating image for section ${i+1}/${selectedHeadings.length}: "${headingText}"`);
              setGenerationStatus(`Creating image for section ${i+1}/${selectedHeadings.length}: "${headingText}"`);
              
              try {
                // Create a more specific prompt for the image based on the heading
                const imagePrompt = `${title} - ${headingText}`;
                
                const imageOptions = {
                  prompt: imagePrompt,
                  model: imageModel,
                  dimension: dimensions,
                  style: imageStyle,
                  enhancePrompt: true,
                  context: { articleTitle: title, sectionTitle: headingText },
                };
                
                console.log(`Generating image for heading: "${headingText}" with prompt: "${imagePrompt}"`);
                const imageData = await imageClient.generateImage(imageOptions);
                
                if (imageData?.image_url) {
                  // Upload the image to WordPress
                  updateStepStatus('images', 'in-progress', `Uploading image for section "${headingText}"`);
                  const response = await fetch(imageData.image_url);
                  const blob = await response.blob();
                  const formData = new FormData();
                  formData.append('file', blob, `section-${Date.now()}-${i}.png`);
                  formData.append('siteId', selectedSite);
                  
                  const uploadResult = await authenticatedRequest(() =>
                    apiClient.uploadMedia(formData)
                  );
                  
                  if (uploadResult?.media_id) {
                    console.log(`Successfully uploaded image for heading ${i+1}: "${headingText}"`);
                    
                    // Get the media URL
                    const mediaUrl = await wpClient.getMediaUrl(uploadResult.media_id);
                    
                    // Insert the image after the heading in the HTML with WordPress-specific classes
                    const img = doc.createElement('img');
                    img.src = mediaUrl;
                    img.alt = headingText; // Set alt text to the heading text, not the article title
                    img.className = `wp-image-${uploadResult.media_id}`;
                    img.style.cssText = 'max-width: 100%; height: auto; margin: 20px 0;';
                    
                    // Insert the image after the heading
                    if (heading.nextSibling) {
                      heading.parentNode.insertBefore(img, heading.nextSibling);
                    } else {
                      heading.parentNode.appendChild(img);
                    }
                  }
                }
              } catch (error) {
                console.error(`Error generating image for section "${headingText}":`, error);
                updateStepStatus('images', 'in-progress', `Failed to generate image for section "${headingText}", continuing`);
                setGenerationStatus(`Failed to generate image for section "${headingText}", continuing...`);
              }
            }
            
            // Use innerHTML instead of serializing the entire document
            processedContent = doc.body.innerHTML;
          } catch (error) {
            console.error('Error processing subheading images:', error);
            updateStepStatus('images', 'in-progress', 'Error processing subheading images, continuing');
            setGenerationStatus('Error processing subheading images, continuing with article creation...');
          }
        }
        
        updateStepStatus('images', 'completed', 'All images processed');
        
        // Final WordPress upload with enhanced feedback
        updateStepStatus('upload', 'in-progress', `Creating WordPress post for "${title}"`);
        setGenerationStatus(`Creating WordPress post for "${title}"`);
        updateArticleProgress(title, 'in-progress', 'upload', 'Publishing to WordPress');

        // Clean the title one more time to ensure no quotes are present
        const cleanTitle = title.replace(/^["'](.*)["']$/, '$1').trim();

        // Ensure featuredMediaId is properly formatted for WordPress
        const featuredMedia = featuredMediaId ? parseInt(featuredMediaId, 10) : 0;
        console.log(`Creating post with title: "${cleanTitle}", category: ${category}, featuredMedia: ${featuredMedia}`);
        
        // Prepare tags for WordPress from SEO keywords
        const tags = keywordMode === 'manual' 
          ? seoKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
          : selectedKeywords;
        
        console.log('Using tags for WordPress:', tags);

        const post = await wpClient.createPost({
          title: cleanTitle, // Use the cleaned title here
          content: processedContent,
          categoryId: category ? parseInt(category, 10) : undefined,
          featuredMediaId: featuredMedia,
          status: 'draft',
          tags: tags
        });

        console.log('WordPress post created:', post);
        updateStepStatus('upload', 'completed', `Article "${title}" published successfully`);
        setGenerationStatus(`✓ Article ${completedArticles}/${totalArticles} "${title}" published successfully!`);
        updateArticleProgress(title, 'completed', 'done', 'Published successfully!');

        // Store the last generated article for preview
        const featuredImageUrl = featuredMediaId ? await wpClient.getMediaUrl(featuredMediaId) : null;
        console.log('Featured image URL for preview:', featuredImageUrl);

        setGeneratedArticle({
          title: title,
          content: processedContent,
          featuredImage: featuredImageUrl,
          link: post.link,
          postId: post.id,
        });
      }
      
      // Only show response dialog and clear loading state after all articles are processed
      setResponseDialogOpen(true);
      setError(null);
    } catch (err) {
      console.error('Error generating article:', err);
      const failedStep = generationSteps.find((step) => step.status === 'pending' || step.status === 'in-progress');
      if (failedStep) {
        updateStepStatus(failedStep.id, 'error', `Error: ${err.message}`, err.message);
      }
      
      setError(err.message || 'Failed to generate article');
      setGenerationStatus('Generation failed. Please check the error message and try again.');
      setResponseDialogOpen(true);
    } finally {
      // Only clear loading state after all operations are complete
      setTimeout(() => {
        setLoading(false);
        setIsGenerating(false);
      }, 1000); // Add a small delay to ensure all UI updates are complete
    }
  };
  
  // Help dialog content
  function HelpDialog() {
    return (
      <Dialog 
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
            background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
          }}>
            <HelpCircle size={24} />
          </Box>
          <Typography variant="h6">How to Use Quick Article Creator</Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setHelpDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
          }}
        >
          <X size={20} />
        </IconButton>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              The Quick Article Creator streamlines your content creation process, allowing you to go from concept to published WordPress draft in minutes. Follow these simple steps to create professional content effortlessly.
            </Typography>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 1: Choose Your Input Method
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Start by selecting how you want to begin your article creation process:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Enter Title:</strong> If you already know your article title, simply type it in and click "Use This Title" to proceed directly to settings.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Use Keyword:</strong> If you have a topic but need title ideas, enter your keyword and select a title type (General, Question, How-To, List, or SEO). Click "Generate Title Options" to get multiple professional title suggestions.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Select Titles:</strong> You can select one or more titles from the generated list by clicking on them. Each selected title will become a separate article.
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 2: Article Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Customize how your article will be written:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Writing Style:</strong> Choose between Informative (fact-based), Conversational (friendly, engaging), or Professional (formal, business-oriented).
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Perspective:</strong> Select First Person (I/We), Second Person (You), or Third Person (They) to match your brand voice.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Article Length:</strong> Choose Short (500-700 words), Medium (1000-1200 words), or Long (1500-1800 words) based on your needs.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>FAQ Section:</strong> Toggle on to include a helpful FAQ section at the end of your article.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Number of Subheadings:</strong> Choose how many sections your article should have (1-15) to control the article structure.
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 3: WordPress Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Select where to publish your finished article:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>WordPress Site:</strong> Choose which of your connected WordPress sites to publish to.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Category:</strong> Select the appropriate category for your article to ensure proper organization on your site.
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 4: Image Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Add AI-generated images to enhance your article:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Generate Featured Image:</strong> Toggle on to create an AI-generated featured image that matches your article title.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Add Subheading Images:</strong> Toggle on to automatically insert relevant images throughout your article under key subheadings.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Image Generation Settings:</strong> Customize the AI model, style, and dimensions to match your site's aesthetic.
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 5: Generate and Publish
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                When you're ready, click the "Generate Article" button and the AI will:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Create the content:</strong> Write a complete, well-structured article based on your selected settings, title, and preferences.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Generate images:</strong> Create and place featured and section images as requested.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Publish to WordPress:</strong> Save the finished article as a draft in your selected WordPress site and category.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Preview your work:</strong> After generation, you can preview your article before visiting WordPress to make any final edits.
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
              Tip: For batch creation, you can select multiple titles from the generated list to create several articles at once!
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setHelpDialogOpen(false)}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4, 
              py: 1.2,
              background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
              boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #CC4414, #E85E35)',
              }
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        px: 3,
        pb: 2,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '96%',
          height: '1px',
          background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.01), rgba(59, 130, 246, 0.2) 20%, rgba(59, 130, 246, 0.2) 80%, rgba(59, 130, 246, 0.01))',
          borderRadius: '1px',
        }
      }}>
        <PageHeader 
          category="Content Creation"
          title="Quick Article Creator"
          description="Create professional articles in minutes with minimal input. Perfect for beginners or when you need content fast."
          icon={<Zap size={24} color="#E04C16" />} 
        />
        <Box sx={{ mr: 6 }}>
          <Button
            onClick={() => setHelpDialogOpen(true)}
            startIcon={<HelpCircle size={18} />}
            sx={{
              bgcolor: 'rgba(224, 76, 22, 0.08)',
              color: '#E04C16',
              borderRadius: '12px',
              px: 3,
              py: 1,
              minWidth: '140px', // Add minimum width to prevent text wrapping
              fontWeight: 600,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap', // Prevent text wrapping
              border: '1px solid rgba(224, 76, 22, 0.12)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'rgba(224, 76, 22, 0.12)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.12)'
              }
            }}
          >
            How to Use
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3} sx={{ px: 2 }}>
        {/* Left Column - Input and Title Generation */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                1
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  Choose Your Input Method
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Start with a title or generate options from a keyword
                </Typography>
              </Box>
            </Box>
            
            <Tabs
              value={inputMode}
              onChange={(e, newValue) => setInputMode(newValue)}
              sx={{ mb: 3 }}
            >
              <Tab 
                value="title" 
                label="Enter Title" 
                icon={<FileText size={16} />} 
                iconPosition="start"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }}
              />
              <Tab 
                value="keyword" 
                label="Use Keyword" 
                icon={<Search size={16} />} 
                iconPosition="start"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }}
              />
            </Tabs>
            
            {inputMode === 'title' ? (
              <Box>
                <TextField
                  fullWidth
                  label="Enter Your Article Title"
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  placeholder="e.g., 10 Ways to Improve Your Productivity"
                  sx={{ mb: 3 }}
                />
                {titleInput && (
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Plus size={18} />}
                    onClick={() => {
                      // Clean the title by removing any quotes
                      const cleanedTitle = titleInput.replace(/^["'](.*)["']$/, '$1').trim();
                      setSelectedTitles([cleanedTitle]);
                      setGeneratedTitles([cleanedTitle]);
                    }}
                    sx={{
                      py: 1.5,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: '#3B82F6',
                      '&:hover': { bgcolor: '#2563EB' }
                    }}
                  >
                    Use This Title
                  </Button>
                )}
              </Box>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  label="Enter Keyword or Topic"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="e.g., productivity, fitness, marketing"
                  sx={{ mb: 3 }}
                />
                
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Type
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { value: 'general', label: 'General', icon: <FileText size={16} /> },
                    { value: 'question', label: 'Question', icon: <MessageSquare size={16} /> },
                    { value: 'how-to', label: 'How-To', icon: <Lightbulb size={16} /> },
                    { value: 'list', label: 'List', icon: <FileText size={16} /> },
                    { value: 'seo', label: 'SEO', icon: <Search size={16} /> }
                  ].map((option) => (
                    <Grid item xs={6} sm={4} key={option.value}>
                      <Button
                        variant={titleType === option.value ? 'contained' : 'outlined'}
                        startIcon={option.icon}
                        onClick={() => setTitleType(option.value)}
                        fullWidth
                        sx={{
                          py: 1,
                          textTransform: 'none',
                          borderRadius: '8px',
                          ...(titleType === option.value ? {
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' }
                          } : {
                            color: '#4B5563',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.04)' }
                          })
                        }}
                      >
                        {option.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Wand2 size={18} />}
                  onClick={handleGenerateTitles}
                  disabled={isGeneratingTitles || !keyword}
                  sx={{
                    py: 1.5,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#3B82F6',
                    '&:hover': { bgcolor: '#2563EB' }
                  }}
                >
                  {isGeneratingTitles ? 'Generating...' : 'Generate Title Options'}
                </Button>
              </Box>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            
            {generatedTitles.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Select Titles 
                    <Chip 
                      label={`${selectedTitles.length} selected`} 
                      size="small" 
                      color={selectedTitles.length > 0 ? "primary" : "default"}
                      sx={{ ml: 1, fontWeight: 500 }}
                    />
                  </Typography>
                </Box>
                
                <List sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  maxHeight: '300px',
                  overflow: 'auto',
                  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.02)'
                }}>
                  {generatedTitles.map((title, index) => (
                    <ListItem
                      key={index}
                      button
                      selected={selectedTitles.includes(title)}
                      onClick={isGenerating ? null : () => handleTitleToggle(title)}
                      sx={{
                        borderRadius: '8px',
                        mb: 0.5,
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(59, 130, 246, 0.08)',
                          '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.12)' }
                        },
                        '&:hover': {
                          bgcolor: isGenerating ? 'transparent' : 'rgba(0, 0, 0, 0.02)',
                          transform: isGenerating ? 'none' : 'translateX(2px)'
                        },
                        opacity: isGenerating && !selectedTitles.includes(title) ? 0.5 : 1,
                        pointerEvents: isGenerating ? 'none' : 'auto'
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {isGenerating && selectedTitles.includes(title) ? (
                          articleProgress[title]?.status === 'completed' ? (
                            <CheckCircle size={18} color="#10B981" />
                          ) : articleProgress[title]?.status === 'in-progress' ? (
                            articleProgress[title]?.step === 'content' ? (
                              <FileText size={18} color="#3B82F6" />
                            ) : articleProgress[title]?.step === 'images' ? (
                              <Image size={18} color="#3B82F6" />
                            ) : articleProgress[title]?.step === 'upload' ? (
                              <ArrowRight size={18} color="#3B82F6" />
                            ) : (
                              <Settings size={18} color="#3B82F6" />
                            )
                          ) : articleProgress[title]?.status === 'error' ? (
                            <AlertCircle size={18} color="#EF4444" />
                          ) : (
                            <Clock size={18} color="#6B7280" />
                          )
                        ) : selectedTitles.includes(title) ? (
                          <CheckCircle size={18} color="#3B82F6" />
                        ) : (
                          <Bookmark size={18} color="#6B7280" />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={isGenerating && selectedTitles.includes(title) && articleProgress[title] ? 
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              color: '#1F2937'
                            }}>
                              {title}
                            </Typography>
                            <Typography sx={{ 
                              fontSize: '0.75rem',
                              color: articleProgress[title]?.status === 'error' ? '#EF4444' : 
                                     articleProgress[title]?.status === 'completed' ? '#10B981' : '#3B82F6',
                              mt: 0.5
                            }}>
                              {articleProgress[title]?.message || 'Waiting...'}
                            </Typography>
                          </Box>
                          : title
                        } 
                        primaryTypographyProps={{ 
                          fontSize: '0.9rem',
                          fontWeight: selectedTitles.includes(title) ? 600 : 400,
                          color: selectedTitles.includes(title) ? '#1F2937' : '#4B5563'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                {selectedTitles.length > 0 && subheadingType === 'custom' && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                      Subheadings
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Customize the subheadings for your article(s). Each line will be a separate subheading.
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      value={customSubheadings}
                      onChange={(e) => setCustomSubheadings(e.target.value)}
                      placeholder="Enter one subheading per line..."
                      sx={{ mb: 2 }}
                    />
                    <FormControl component="fieldset" sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Subheading Generation
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Button
                            variant={subheadingType === 'auto' ? 'contained' : 'outlined'}
                            fullWidth
                            size="small"
                            onClick={() => setSubheadingType('auto')}
                            sx={{
                              textTransform: 'none',
                              ...(subheadingType === 'auto' ? {
                                bgcolor: '#3B82F6',
                                '&:hover': { bgcolor: '#2563EB' }
                              } : {
                                color: '#4B5563',
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.04)' }
                              })
                            }}
                          >
                            Auto-generate
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            variant={subheadingType === 'custom' ? 'contained' : 'outlined'}
                            fullWidth
                            size="small"
                            onClick={() => setSubheadingType('custom')}
                            sx={{
                              textTransform: 'none',
                              ...(subheadingType === 'custom' ? {
                                bgcolor: '#3B82F6',
                                '&:hover': { bgcolor: '#2563EB' }
                              } : {
                                color: '#4B5563',
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.04)' }
                              })
                            }}
                          >
                            Custom
                          </Button>
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
          
          {/* Article Settings */}
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                2
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  Article Settings
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Customize how your article will be written
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Writing Style</InputLabel>
                  <Select
                    value={writingStyle}
                    label="Writing Style"
                    onChange={(e) => setWritingStyle(e.target.value)}
                  >
                    <MenuItem value="informative">Informative</MenuItem>
                    <MenuItem value="conversational">Conversational</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Perspective</InputLabel>
                  <Select
                    value={perspective}
                    label="Perspective"
                    onChange={(e) => setPerspective(e.target.value)}
                  >
                    <MenuItem value="first">First Person (I/We)</MenuItem>
                    <MenuItem value="second">Second Person (You)</MenuItem>
                    <MenuItem value="third">Third Person (They)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Article Length</InputLabel>
                  <Select
                    value={articleLength}
                    label="Article Length"
                    onChange={(e) => setArticleLength(e.target.value)}
                  >
                    <MenuItem value="short">Short (500-700 words)</MenuItem>
                    <MenuItem value="medium">Medium (1000-1200 words)</MenuItem>
                    <MenuItem value="long">Long (1500-1800 words)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={includeFAQs} 
                      onChange={(e) => setIncludeFAQs(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Include FAQ Section"
                  sx={{ mt: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Number of Subheadings</InputLabel>
                  <Select
                    value={subheadingCount}
                    label="Number of Subheadings"
                    onChange={(e) => setSubheadingCount(e.target.value)}
                  >
                    {Array.from({ length: 15 }, (_, i) => i + 1).map(number => (
                      <MenuItem key={number} value={number}>
                        {number} {number === 1 ? 'Subheading' : 'Subheadings'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Right Column - WordPress Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                3
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  WordPress Settings
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Select where to publish your finished article
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>WordPress Site</InputLabel>
                  <Select
                    value={selectedSite}
                    label="WordPress Site"
                    onChange={(e) => setSelectedSite(e.target.value)}
                  >
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={!selectedSite}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Image Settings */}
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                4
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  Image Settings
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Add AI-generated images to your article
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={includeImages} 
                    onChange={(e) => setIncludeImages(e.target.checked)}
                    color="primary"
                  />
                }
                label="Generate Featured Image"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Create an AI-generated featured image for your article
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={includeSubheadingImages} 
                    onChange={(e) => setIncludeSubheadingImages(e.target.checked)}
                    color="primary"
                  />
                }
                label="Add Subheading Images"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Generate images for up to 3 subheadings in each article
              </Typography>
            </Box>
            
            {(includeImages || includeSubheadingImages) && (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                mb: 3
              }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Image Generation Settings
                </Typography>
                
                <ImageControls
                  model={imageModel}
                  setModel={setImageModel}
                  style={imageStyle}
                  setStyle={setImageStyle}
                  dimension={dimensions}
                  setDimension={setDimensions}
                  disabled={false}
                />
              </Box>
            )}
          </Paper>
          
          {/* Generate Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Sparkles size={20} />}
              onClick={handleGenerateArticle}
              disabled={selectedTitles.length === 0 || !selectedSite || loading}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transition: 'all 0.6s ease',
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #CC4414, #E85E35)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 16px rgba(224, 76, 22, 0.4)',
                  '&:before': {
                    left: '100%',
                  }
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <span>Generating Articles...</span>
                </Box>
              ) : (
                <>
                  {selectedTitles.length > 1 ? (
                    <>Generate {selectedTitles.length} Articles</>
                  ) : (
                    <>Generate Article</>
                  )}
                </>
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Loading Overlay */}
      <LoadingOverlay visible={loading} status={generationStatus} steps={generationSteps} />
      
      {/* Help Dialog */}
      <HelpDialog />
      
      {/* Response Dialog */}
      <ArticleResponseDialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        success={!!generatedArticle}
        error={error}
        articleData={generatedArticle}
        articlesCount={selectedTitles.length}
        onPreview={() => {
          setResponseDialogOpen(false);
          setPreviewDialogOpen(true);
        }}
      />
      
      {/* Preview Dialog */}
      <ArticlePreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        article={generatedArticle}
        articlesCount={selectedTitles.length}
      />

    </Box>
  );
};

export default QuickArticleCreator;
